export const DEFAULT_CTA_LANGUAGE = 'en-US';

// Справочник языков CTA. Список можно безопасно расширять новыми локалями.
export const CTA_LANGUAGE_OPTIONS = [
  {value: 'en-US', label: 'English (United States)'},
  {value: 'de', label: 'German'},
  {value: 'fr', label: 'French'},
  {value: 'es', label: 'Spanish'},
  {value: 'it', label: 'Italian'},
  {value: 'pt', label: 'Portuguese'},
] as const;

// Ключи CTA храним отдельно от текста, чтобы можно было менять переводы
// и не ломать уже сохраненные шаблоны.
export const CTA_DICTIONARY = {
  LEARN_MORE: {
    'en-US': 'Learn more',
    de: 'Mehr erfahren',
    fr: 'En savoir plus',
    es: 'Mas informacion',
    it: 'Scopri di piu',
    pt: 'Saiba mais',
  },
  SHOP_NOW: {
    'en-US': 'Shop now',
    de: 'Jetzt kaufen',
    fr: 'Acheter',
    es: 'Comprar ahora',
    it: 'Acquista ora',
    pt: 'Comprar agora',
  },
  SIGN_UP: {
    'en-US': 'Sign up',
    de: 'Registrieren',
    fr: "S'inscrire",
    es: 'Registrarse',
    it: 'Registrati',
    pt: 'Inscreva-se',
  },
  CONTACT_US: {
    'en-US': 'Contact us',
    de: 'Kontakt',
    fr: 'Contactez-nous',
    es: 'Contactanos',
    it: 'Contattaci',
    pt: 'Fale conosco',
  },
  START_NOW: {
    'en-US': 'Start now',
    'de-DE': 'Jetzt starten',
    'fr-FR': 'Commencer',
    'es-ES': 'Comenzar ahora',
    'it-IT': 'Inizia ora',
    'pt-PT': 'Comece agora',
  },
  VISIT_SITE: {
    'en-US': 'Visit site',
    de: 'Website besuchen',
    fr: 'Visiter le site',
    es: 'Visitar sitio',
    it: 'Visita il sito',
    pt: 'Visitar site',
  },
} as const;

export type CallToActionLanguage = typeof CTA_LANGUAGE_OPTIONS[number]['value'];
export type CallToActionKey = keyof typeof CTA_DICTIONARY;
export type CallToActionLanguageOption = {
  value: string;
  label: string;
};
export type CallToActionCatalogItem = {
  key: string;
  is_active?: boolean;
  sort?: number;
  texts: Record<string, string>;
};

export const CTA_EMPTY_OPTION = {value: '', label: 'Не задано'};

export const isCallToActionKey = (value: unknown): value is CallToActionKey =>
  typeof value === 'string' && value.toUpperCase() in CTA_DICTIONARY;

const normalizeCallToActionKey = (value?: string | null): string =>
  String(value || '').trim().toUpperCase();

export const CTA_FALLBACK_LANGUAGE_OPTIONS: CallToActionLanguageOption[] = CTA_LANGUAGE_OPTIONS.map(option => ({
  value: option.value,
  label: option.label,
}));

const CTA_LANGUAGE_ALIASES: Record<string, string> = {
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  pt: 'pt-PT',
};

export const CTA_FALLBACK_ITEMS: CallToActionCatalogItem[] = Object.entries(CTA_DICTIONARY).map(([key, texts]) => ({
  key,
  is_active: true,
  sort: 100,
  texts: {...texts},
}));

export const normalizeCallToActionLanguage = (
  value: unknown,
  languages: CallToActionLanguageOption[] = CTA_FALLBACK_LANGUAGE_OPTIONS,
): string => {
  const rawValue = String(value || '').trim();
  const normalizedValue = CTA_LANGUAGE_ALIASES[rawValue] || rawValue;

  return languages.some(option => option.value === normalizedValue)
    ? normalizedValue
    : DEFAULT_CTA_LANGUAGE;
};

export const resolveCallToActionText = (
  key?: string | null,
  language?: string | null,
  items: CallToActionCatalogItem[] = CTA_FALLBACK_ITEMS,
  languages: CallToActionLanguageOption[] = CTA_FALLBACK_LANGUAGE_OPTIONS,
): string => {
  const normalizedKey = normalizeCallToActionKey(key);
  if (!normalizedKey) {
    return '';
  }

  const normalizedLanguage = normalizeCallToActionLanguage(language, languages);
  const item = items.find(entry => entry.key.toUpperCase() === normalizedKey);

  return item?.texts?.[normalizedLanguage] ?? item?.texts?.[DEFAULT_CTA_LANGUAGE] ?? '';
};

export const getCallToActionOptions = (
  language?: string | null,
  items: CallToActionCatalogItem[] = CTA_FALLBACK_ITEMS,
  languages: CallToActionLanguageOption[] = CTA_FALLBACK_LANGUAGE_OPTIONS,
) => {
  const normalizedLanguage = normalizeCallToActionLanguage(language, languages);

  return [
    CTA_EMPTY_OPTION,
    ...items
      .filter(item => item.is_active !== false)
      .sort((a, b) => (a.sort ?? 100) - (b.sort ?? 100) || a.key.localeCompare(b.key))
      .map(item => ({
      value: item.key,
      label: item.texts?.[normalizedLanguage] ?? item.texts?.[DEFAULT_CTA_LANGUAGE] ?? item.key,
    })),
  ];
};

export const findCallToActionKeyByText = (
  text?: string | null,
  language?: string | null,
  items: CallToActionCatalogItem[] = CTA_FALLBACK_ITEMS,
  languages: CallToActionLanguageOption[] = CTA_FALLBACK_LANGUAGE_OPTIONS,
): string => {
  const normalizedText = String(text || '').trim().toLowerCase();
  if (normalizedText === '') {
    return '';
  }

  const normalizedLanguage = normalizeCallToActionLanguage(language, languages);

  for (const item of items) {
    const localized = String(
      item.texts?.[normalizedLanguage]
        ?? item.texts?.[DEFAULT_CTA_LANGUAGE]
        ?? '',
    )
      .trim()
      .toLowerCase();

    if (localized === normalizedText) {
      return item.key;
    }
  }

  for (const item of items) {
    const hasMatch = Object.values(item.texts || {}).some(value => value.trim().toLowerCase() === normalizedText);
    if (hasMatch) {
      return item.key;
    }
  }

  return '';
};
